import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  ArrowLeft, Users, GraduationCap, BookOpen, TrendingUp, Download,
  BarChart3, PieChart, Activity, Award, Target, Loader2, RefreshCw,
  UserPlus, FileText, Calendar, Star, ArrowUpRight, ArrowDownRight, Mail, Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EdpiesLogo from '@/components/EdpiesLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [testAnalytics, setTestAnalytics] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [weeklyEmailInfo, setWeeklyEmailInfo] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  useEffect(() => {
    loadAllAnalytics();
  }, []);

  const loadAllAnalytics = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Fetch in parallel — but isolate each call so one failure doesn't
    // hide the rest of the dashboard.
    const [overviewRes, usersRes, testsRes, engagementRes, subscriptionsRes] = await Promise.allSettled([
      axios.get(`${API}/admin/analytics/overview`, { headers }),
      axios.get(`${API}/admin/analytics/users`, { headers }),
      axios.get(`${API}/admin/analytics/tests`, { headers }),
      axios.get(`${API}/admin/analytics/engagement`, { headers }),
      axios.get(`${API}/admin/analytics/subscriptions`, { headers })
    ]);

    // Check for admin permission first
    const firstFailure = [overviewRes, usersRes, testsRes, engagementRes, subscriptionsRes].find(r => r.status === 'rejected');
    if (firstFailure?.reason?.response?.status === 403) {
      toast.error('Admin access required');
      navigate('/');
      setLoading(false);
      return;
    }

    if (overviewRes.status === 'fulfilled') {
      setOverview(overviewRes.value.data);
    } else {
      console.error('Overview analytics failed:', overviewRes.reason);
    }
    if (usersRes.status === 'fulfilled') {
      setUserAnalytics(usersRes.value.data);
    } else {
      console.error('User analytics failed:', usersRes.reason);
    }
    if (testsRes.status === 'fulfilled') {
      setTestAnalytics(testsRes.value.data);
    } else {
      console.error('Test analytics failed:', testsRes.reason);
    }
    if (engagementRes.status === 'fulfilled') {
      setEngagement(engagementRes.value.data);
    } else {
      console.error('Engagement analytics failed:', engagementRes.reason);
    }
    if (subscriptionsRes.status === 'fulfilled') {
      setSubscriptions(subscriptionsRes.value.data);
    } else {
      console.error('Subscription analytics failed:', subscriptionsRes.reason);
    }

    const failedCount = [overviewRes, usersRes, testsRes, engagementRes, subscriptionsRes].filter(r => r.status === 'rejected').length;
    if (failedCount === 5) {
      toast.error('Failed to load analytics');
    } else if (failedCount > 0) {
      toast.warning(`${failedCount} of 5 analytics sections failed to load`);
    }

    setLoading(false);
  };

  const exportData = async (type, format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export/${type}?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`Exported ${type} data`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const loadWeeklyEmailInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/weekly-summary/next-run`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyEmailInfo(res.data);
    } catch (err) {
      console.error('Failed to load weekly email info', err);
    }
  };

  const handleSendAllWeeklySummaries = async () => {
    if (!window.confirm('Send weekly summary emails to ALL parents right now? This is in addition to the scheduled Monday 9 AM London send.')) {
      return;
    }
    setSendingAll(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/weekly-summary/send-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Emails sent: ${res.data.emails_sent} / ${res.data.total_students}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingAll(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'emails') {
      loadWeeklyEmailInfo();
    }
  }, [activeTab]);

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'purple' }) => (
    <Card className="shadow-lg hover:shadow-xl transition-shadow">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs md:text-sm text-gray-500 mb-1 truncate">{title}</p>
            <p className="text-2xl md:text-3xl font-bold text-gray-800">{value}</p>
            {subtitle && <p className="text-xs md:text-sm text-gray-600 mt-1 truncate">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-xs md:text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {trend === 'up' ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`p-2 md:p-3 rounded-xl bg-${color}-100 shrink-0`}>
            <Icon className={`h-4 w-4 md:h-6 md:w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const SimpleBarChart = ({ data, labelKey, valueKey, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d[valueKey])) || 1;
    return (
      <div className="space-y-3">
        {data.map((item, idx) => (
          <div key={idx}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">{item[labelKey]}</span>
              <span className="font-medium">{item[valueKey]}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                style={{ width: `${(item[valueKey] / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-white mx-auto" />
          <p className="mt-4 text-white text-lg">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/tutors')} className="text-white hover:bg-white/20">
                <ArrowLeft className="h-5 w-5 mr-2" />
                Admin
              </Button>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-6 w-6" />
                <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={loadAllAnalytics}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <EdpiesLogo size="sm" />
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-6 h-auto">
            <TabsTrigger value="overview" className="flex-col md:flex-row py-2 md:py-1.5 text-[11px] md:text-sm">
              <PieChart className="h-4 w-4 md:mr-2 mb-1 md:mb-0" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-col md:flex-row py-2 md:py-1.5 text-[11px] md:text-sm">
              <Users className="h-4 w-4 md:mr-2 mb-1 md:mb-0" />
              Users
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex-col md:flex-row py-2 md:py-1.5 text-[11px] md:text-sm">
              <Target className="h-4 w-4 md:mr-2 mb-1 md:mb-0" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="export" className="flex-col md:flex-row py-2 md:py-1.5 text-[11px] md:text-sm">
              <Download className="h-4 w-4 md:mr-2 mb-1 md:mb-0" />
              <span className="hidden xs:inline">Export</span>
              <span className="xs:hidden">Export</span>
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-weekly-emails" className="flex-col md:flex-row py-2 md:py-1.5 text-[11px] md:text-sm">
              <Mail className="h-4 w-4 md:mr-2 mb-1 md:mb-0" />
              Emails
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* HERO: Active Users — the 2 most important numbers at a glance */}
            <div className="grid grid-cols-2 gap-3 md:gap-6">
              {/* Active Students */}
              <Card
                className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white overflow-hidden relative"
                data-testid="active-students-card"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="h-4 w-4 md:h-5 md:w-5 opacity-80" />
                    <p className="text-xs md:text-sm font-medium opacity-90 uppercase tracking-wide">
                      Active Students
                    </p>
                  </div>
                  <p className="text-4xl md:text-6xl font-bold leading-none" data-testid="active-students-count">
                    {overview?.users?.activeStudents ?? 0}
                  </p>
                  <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm opacity-90">
                    <span>of {overview?.users?.students ?? 0} total</span>
                    {(overview?.users?.pendingStudents ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-300"></span>
                        {overview.users.pendingStudents} pending
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Active Parents */}
              <Card
                className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white overflow-hidden relative"
                data-testid="active-parents-card"
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 md:h-5 md:w-5 opacity-80" />
                    <p className="text-xs md:text-sm font-medium opacity-90 uppercase tracking-wide">
                      Active Parents
                    </p>
                  </div>
                  <p className="text-4xl md:text-6xl font-bold leading-none" data-testid="active-parents-count">
                    {overview?.users?.activeParents ?? 0}
                  </p>
                  <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm opacity-90">
                    <span>of {overview?.users?.parents ?? 0} total</span>
                    {(overview?.users?.pendingParents ?? 0) > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-300"></span>
                        {overview.users.pendingParents} pending
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Supporting Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <StatCard
                title="New This Week"
                value={overview?.users?.newLast7Days || 0}
                subtitle={`${overview?.users?.newLast30Days || 0} this month`}
                icon={UserPlus}
                color="purple"
              />
              <StatCard
                title="Engaged (7d)"
                value={overview?.users?.engagedLast7Days || 0}
                subtitle={`${overview?.users?.engagedLast30Days || 0} in last 30 days`}
                icon={Activity}
                color="orange"
              />
              <StatCard
                title="Tests Completed"
                value={overview?.engagement?.totalTests || 0}
                subtitle={`${overview?.engagement?.testsLast7Days || 0} this week`}
                icon={BookOpen}
                color="blue"
              />
              <StatCard
                title="Average Score"
                value={`${overview?.engagement?.averageScore || 0}%`}
                subtitle="Across all tests"
                icon={Target}
                color="green"
              />
            </div>

            {/* User Breakdown & Engagement */}
            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <Card className="shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Users className="h-5 w-5 text-purple-600" />
                    User Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <GraduationCap className="h-5 w-5 text-blue-600" />
                        </div>
                        <span className="font-medium text-sm md:text-base">Students</span>
                      </div>
                      <span className="text-xl md:text-2xl font-bold text-blue-600">{overview?.users?.students || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Users className="h-5 w-5 text-green-600" />
                        </div>
                        <span className="font-medium text-sm md:text-base">Parents</span>
                      </div>
                      <span className="text-xl md:text-2xl font-bold text-green-600">{overview?.users?.parents || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Award className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-medium text-sm md:text-base">Tutors</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xl md:text-2xl font-bold text-purple-600">{overview?.tutors?.total || 0}</span>
                        <p className="text-xs text-gray-500">
                          {overview?.tutors?.approved || 0} approved, {overview?.tutors?.pending || 0} pending
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Activity className="h-5 w-5 text-purple-600" />
                    Engagement Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-600">Challenger Sessions</span>
                      <span className="font-bold">{overview?.engagement?.challengerSessions || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-600">AI Chat Messages</span>
                      <span className="font-bold">{overview?.engagement?.chatMessages || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-600">Custom Question Banks</span>
                      <span className="font-bold">{overview?.content?.customQuestionBanks || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm md:text-base">
                      <span className="text-gray-600">Tests This Week</span>
                      <span className="font-bold text-green-600">{overview?.engagement?.testsLast7Days || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subscription Breakdown */}
            {subscriptions && (
              <Card className="shadow-lg" data-testid="subscription-breakdown-card">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                        <Users className="h-5 w-5 text-purple-600" />
                        Subscribers by Tier
                      </CardTitle>
                      <CardDescription className="text-xs md:text-sm mt-1">
                        {subscriptions.total_users} total users · {subscriptions.paying_users} paying · est. MRR £{subscriptions.estimated_mrr}
                      </CardDescription>
                    </div>
                    <Badge variant="outline" className="text-xs" data-testid="subscription-mrr-badge">
                      £{subscriptions.estimated_mrr}/mo est.
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Tier cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3">
                    {subscriptions.tier_breakdown.map((t) => {
                      const tierColors = {
                        pro: 'from-amber-400 to-yellow-500 text-amber-900',
                        advance: 'from-purple-500 to-fuchsia-500 text-white',
                        beginner: 'from-sky-400 to-blue-500 text-white',
                        promotional: 'from-emerald-400 to-teal-500 text-white',
                        free: 'from-gray-300 to-gray-400 text-gray-800',
                        admin: 'from-slate-600 to-slate-800 text-white',
                        none: 'from-gray-100 to-gray-200 text-gray-700',
                      };
                      return (
                        <div
                          key={t.tier}
                          data-testid={`tier-cell-${t.tier}`}
                          className={`rounded-xl p-3 bg-gradient-to-br ${tierColors[t.tier] || tierColors.none} shadow-sm`}
                        >
                          <p className="text-[10px] md:text-xs uppercase tracking-wide opacity-80 truncate">{t.label}</p>
                          <p className="text-2xl md:text-3xl font-bold mt-1" data-testid={`tier-count-${t.tier}`}>
                            {t.count}
                          </p>
                          <p className="text-[10px] md:text-xs opacity-80 mt-0.5">{t.percentage}%</p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Plan breakdown list */}
                  {subscriptions.plan_breakdown.length > 0 && (
                    <div className="pt-4 border-t">
                      <p className="text-xs md:text-sm font-semibold text-gray-700 mb-3">By Plan</p>
                      <div className="space-y-2">
                        {subscriptions.plan_breakdown.map((p) => (
                          <div
                            key={p.planId}
                            className="flex items-center justify-between text-sm py-1"
                            data-testid={`plan-row-${p.planId}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate text-gray-700">{p.name}</span>
                              {p.billing && (
                                <Badge variant="outline" className="text-[10px] py-0">
                                  {p.billing}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {p.price > 0 && (
                                <span className="text-xs text-gray-500">£{p.price}</span>
                              )}
                              <span className="font-semibold text-gray-900 min-w-[2.5rem] text-right">{p.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <UserPlus className="h-5 w-5 text-purple-600" />
                  Registration Trend (Last 14 Days)
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">Daily new user registrations by type</CardDescription>
              </CardHeader>
              <CardContent>
                {userAnalytics?.registrationTrend?.length > 0 ? (() => {
                  const days = userAnalytics.registrationTrend.slice(-14);
                  const maxCount = Math.max(1, ...days.map(d => d.count || 0));
                  const totals = days.reduce(
                    (acc, d) => ({
                      count: acc.count + (d.count || 0),
                      students: acc.students + (d.students || 0),
                      parents: acc.parents + (d.parents || 0),
                      tutors: acc.tutors + (d.tutors || 0)
                    }),
                    { count: 0, students: 0, parents: 0, tutors: 0 }
                  );
                  const formatDate = (iso) => {
                    try {
                      const d = new Date(iso);
                      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                    } catch { return iso.slice(5); }
                  };
                  return (
                    <div className="space-y-4">
                      {/* 14-day totals summary (tap-target badges, mobile-first) */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                        <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-center">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase tracking-wide">Total</p>
                          <p className="text-xl md:text-2xl font-bold text-gray-800" data-testid="reg-total-14d">{totals.count}</p>
                        </div>
                        <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-center">
                          <p className="text-[10px] md:text-xs text-blue-600 uppercase tracking-wide">Students</p>
                          <p className="text-xl md:text-2xl font-bold text-blue-700">{totals.students}</p>
                        </div>
                        <div className="rounded-xl bg-green-50 border border-green-100 p-3 text-center">
                          <p className="text-[10px] md:text-xs text-green-600 uppercase tracking-wide">Parents</p>
                          <p className="text-xl md:text-2xl font-bold text-green-700">{totals.parents}</p>
                        </div>
                        <div className="rounded-xl bg-purple-50 border border-purple-100 p-3 text-center">
                          <p className="text-[10px] md:text-xs text-purple-600 uppercase tracking-wide">Tutors</p>
                          <p className="text-xl md:text-2xl font-bold text-purple-700">{totals.tutors}</p>
                        </div>
                      </div>

                      {/* Mobile-first vertical-bar chart — one row per day, stacked segments scale as % of max */}
                      <div className="space-y-2" data-testid="reg-trend-list">
                        {days.map((day, idx) => {
                          const widthPct = Math.max(4, Math.round(((day.count || 0) / maxCount) * 100));
                          return (
                            <div key={idx} className="flex items-center gap-2 md:gap-3">
                              <span className="text-[11px] md:text-sm text-gray-500 w-14 md:w-20 shrink-0">
                                {formatDate(day.date)}
                              </span>
                              <div className="flex-1 relative h-7 md:h-8 bg-gray-100 rounded-md overflow-hidden">
                                <div
                                  className="h-full flex"
                                  style={{ width: `${widthPct}%`, minWidth: day.count > 0 ? '8%' : '0' }}
                                >
                                  {day.students > 0 && (
                                    <div
                                      className="h-full bg-blue-500"
                                      style={{ flexGrow: day.students, minWidth: '4px' }}
                                      title={`${day.students} student(s)`}
                                    />
                                  )}
                                  {day.parents > 0 && (
                                    <div
                                      className="h-full bg-green-500"
                                      style={{ flexGrow: day.parents, minWidth: '4px' }}
                                      title={`${day.parents} parent(s)`}
                                    />
                                  )}
                                  {day.tutors > 0 && (
                                    <div
                                      className="h-full bg-purple-500"
                                      style={{ flexGrow: day.tutors, minWidth: '4px' }}
                                      title={`${day.tutors} tutor(s)`}
                                    />
                                  )}
                                </div>
                              </div>
                              <span className="text-xs md:text-sm font-semibold text-gray-800 w-8 md:w-10 text-right tabular-nums">
                                {day.count}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Legend */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-3 border-t">
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-blue-500 rounded-sm" />
                          <span className="text-xs md:text-sm text-gray-600">Students</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-green-500 rounded-sm" />
                          <span className="text-xs md:text-sm text-gray-600">Parents</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-purple-500 rounded-sm" />
                          <span className="text-xs md:text-sm text-gray-600">Tutors</span>
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <p className="text-gray-500 text-center py-8 text-sm">No registration data available</p>
                )}
              </CardContent>
            </Card>

            {/* Top Performers & Most Active */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-yellow-500" />
                    Top Performers
                  </CardTitle>
                  <CardDescription>Highest average scores (min 3 tests)</CardDescription>
                </CardHeader>
                <CardContent>
                  {engagement?.topPerformers?.length > 0 ? (
                    <div className="space-y-3">
                      {engagement.topPerformers.map((user, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-gray-300'}`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-xs text-gray-500">{user.testCount} tests</p>
                            </div>
                          </div>
                          <Badge className="bg-green-500">{user.avgScore}%</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    Most Active Users
                  </CardTitle>
                  <CardDescription>By number of tests completed</CardDescription>
                </CardHeader>
                <CardContent>
                  {engagement?.mostActive?.length > 0 ? (
                    <div className="space-y-3">
                      {engagement.mostActive.map((user, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600">
                              {idx + 1}
                            </div>
                            <p className="font-medium">{user.name}</p>
                          </div>
                          <Badge variant="outline">{user.testCount} tests</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-purple-600" />
                    Tests by Stage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testAnalytics?.byStage?.length > 0 ? (
                    <SimpleBarChart 
                      data={testAnalytics.byStage} 
                      labelKey="stage" 
                      valueKey="count" 
                    />
                  ) : (
                    <p className="text-gray-500 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-600" />
                    Tests by Subject
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testAnalytics?.bySubject?.length > 0 ? (
                    <SimpleBarChart 
                      data={testAnalytics.bySubject} 
                      labelKey="subject" 
                      valueKey="count" 
                    />
                  ) : (
                    <p className="text-gray-500 text-center py-4">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  Average Scores by Stage
                </CardTitle>
              </CardHeader>
              <CardContent>
                {testAnalytics?.byStage?.length > 0 ? (
                  <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {testAnalytics.byStage.map((stage, idx) => (
                      <div key={idx} className="text-center p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-500 mb-2">{stage.stage}</p>
                        <p className={`text-3xl font-bold ${stage.avgScore >= 70 ? 'text-green-600' : stage.avgScore >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {stage.avgScore}%
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{stage.count} tests</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Challenger Leaderboard */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-yellow-500" />
                  Challenger Mode Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent>
                {engagement?.challengerTop?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="pb-2 text-gray-500">Rank</th>
                          <th className="pb-2 text-gray-500">Username</th>
                          <th className="pb-2 text-gray-500">Subject</th>
                          <th className="pb-2 text-gray-500">Stage</th>
                          <th className="pb-2 text-gray-500">Score</th>
                          <th className="pb-2 text-gray-500">Difficulty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {engagement.challengerTop.map((entry, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 font-bold">{idx + 1}</td>
                            <td className="py-2">{entry.username}</td>
                            <td className="py-2">{entry.subject}</td>
                            <td className="py-2">{entry.stage}</td>
                            <td className="py-2 font-medium text-green-600">{entry.score}</td>
                            <td className="py-2">
                              <Badge variant="outline">{entry.difficulty}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No challenger data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-purple-600" />
                  Export Data
                </CardTitle>
                <CardDescription>Download data for external analysis and reporting</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Card className="border-2 border-dashed hover:border-purple-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <Users className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">User Data</h3>
                      <p className="text-sm text-gray-500 mb-4">Export all user registrations with activity metrics</p>
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" onClick={() => exportData('users', 'csv')}>
                          CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => exportData('users', 'json')}>
                          JSON
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-dashed hover:border-purple-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <BookOpen className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">Test Data</h3>
                      <p className="text-sm text-gray-500 mb-4">Export all test attempts with scores and details</p>
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" onClick={() => exportData('tests', 'csv')}>
                          CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => exportData('tests', 'json')}>
                          JSON
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-dashed hover:border-purple-400 transition-colors">
                    <CardContent className="p-6 text-center">
                      <BarChart3 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                      <h3 className="font-bold text-lg mb-2">Analytics Report</h3>
                      <p className="text-sm text-gray-500 mb-4">Comprehensive report with insights & recommendations</p>
                      <Button size="sm" onClick={() => exportData('analytics-report', 'json')}>
                        Download Report
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats for Export Context */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Data Summary</CardTitle>
                <CardDescription>Overview of available data for export</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-3xl font-bold text-purple-600">{overview?.users?.total || 0}</p>
                    <p className="text-sm text-gray-500">Total Users</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{overview?.engagement?.totalTests || 0}</p>
                    <p className="text-sm text-gray-500">Test Records</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{overview?.subscriptions?.active || 0}</p>
                    <p className="text-sm text-gray-500">Subscriptions</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-3xl font-bold text-orange-600">{overview?.tutors?.total || 0}</p>
                    <p className="text-sm text-gray-500">Tutors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Emails Tab */}
          <TabsContent value="emails" className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-purple-600" />
                  Weekly Activity Email Summary
                </CardTitle>
                <CardDescription>
                  Automated parent reports — sent every Monday at 9:00 AM London time
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Scheduler status */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-gray-800">Scheduler Status</p>
                    <Badge
                      variant={weeklyEmailInfo?.scheduler_active ? 'default' : 'destructive'}
                      data-testid="scheduler-status-badge"
                    >
                      {weeklyEmailInfo?.scheduler_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Schedule</span>
                      <span className="font-medium text-gray-900">
                        {weeklyEmailInfo?.schedule || 'Every Monday at 9:00 AM London time'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next Run</span>
                      <span className="font-medium text-gray-900" data-testid="next-run-time">
                        {weeklyEmailInfo?.next_run
                          ? new Date(weeklyEmailInfo.next_run).toLocaleString('en-GB', {
                              weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London'
                            }) + ' London'
                          : 'Loading...'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timezone</span>
                      <span className="font-medium text-gray-900">
                        {weeklyEmailInfo?.timezone || 'Europe/London'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Manual send */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <p className="font-semibold text-gray-800 mb-2">Send Now (Manual Trigger)</p>
                  <p className="text-sm text-gray-600 mb-4">
                    Send this week's summary to all parents immediately. Useful for testing or ad-hoc
                    catch-up runs.
                  </p>
                  <Button
                    onClick={handleSendAllWeeklySummaries}
                    disabled={sendingAll}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="send-all-weekly-btn"
                  >
                    {sendingAll ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Weekly Summary to All Parents
                      </>
                    )}
                  </Button>
                </div>

                {/* Info */}
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 text-sm">
                  <p className="font-semibold text-amber-800 mb-1">How it works</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-900">
                    <li>Every Monday at 9:00 AM London time, the system automatically emails each parent a summary of their child's previous week (Mon–Sun).</li>
                    <li>Each email contains a personal greeting, an activity summary, strengths, and a To-Do list for the coming week.</li>
                    <li>Only students with a linked parent account receive a summary.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminAnalytics;
